import { callApi } from '@/api/callApi';
import {
    API_ENDPOINT,
    API_URL_ORDER_SERVICE,
    API_URL_PRODUCT_SERVICE,
} from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommonTable, {
    Action,
    TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ImagePreview from '@/components/common/media/ImagePreview';
import MediaListing from '@/components/common/media/MediaListing';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import DetailsCard from '@/components/UI/DetailsCard';
import {
    IRefundDetailResponse,
    OrderItem,
} from '@/interfaces/refundDetailResponse.interface';
import { formatDateTime, toFixedThree } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface StatesProp {
    refundDetail: IRefundDetailResponse | null;
}
const RefundDetail = () => {
    const navigate = useNavigate();
    const [states, setStates] = useState<StatesProp>({
        refundDetail: null,
    });
    // const navigate = useNavigate();
    const { refundId } = useParams();
    const handleChangeState = (newState: Partial<typeof states>) => {
        setStates((prev) => ({
            ...prev,
            ...newState,
        }));
    };
    const fetchRefundDetail = async () => {
        try {
            const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.refund}/${refundId}`;
            const res = await callApi(PATH, { method: 'GET' });
            handleChangeState({ refundDetail: res });
        } catch (error) {
            console.log('error is fetching order detail', error);
        }
    };

    useEffect(() => {
        if (refundId) fetchRefundDetail();
    }, [refundId]);

    const refundInfo = [
        { label: 'Order Id', value: states?.refundDetail?.order?.orderId },
        { label: 'Refund ID', value: states?.refundDetail?.refundId },
        { label: 'Status', value: states?.refundDetail?.status },
        { label: 'Amount', value: states?.refundDetail?.amount },
        { label: 'Reason', value: states?.refundDetail?.reason },
        { label: 'Date / Time', value: formatDateTime(states?.refundDetail?.createdAt, 'YYMMMDDHH') },
    ];

    const customerDetail = [
        {
            label: 'First Name',
            value: states?.refundDetail?.order?.personalInfo?.firstName,
        },
        {
            label: 'Last Name',
            value: states?.refundDetail?.order?.personalInfo?.lastName,
        },
        { label: 'Email', value: states?.refundDetail?.order?.personalInfo?.email },
        {
            label: 'Phone Number',
            value: states?.refundDetail?.order?.personalInfo?.phone,
        },
    ];

    const actions: Action<OrderItem>[] = [
        {
            text: 'View',
            onClick: (item) => {
                const url =
                    item?.type === 'bundle'
                        ? `${RoutesLink.collectionDetails}/${item?.bundleDetail?.id}`
                        : `${RoutesLink.productDetail}/${item?.itemDetail?.productId}`;
                navigate(url);
            },
        },
    ];

    const productsColumn: TableColumn<OrderItem>[] = [
        {
            key: 'image',
            header: 'Product',
            render: (item) => {
                const url = item?.itemDetail?.product?.directImageUrl
                    ? item?.itemDetail?.product?.directImageUrl
                    : item?.itemDetail?.product?.images?.[0]?.url;
                const src =
                    item?.type === 'bundle' ? item?.bundleDetail?.media[0]?.url : url;
                return (
                    <ImagePreview
                        src={`${API_URL_PRODUCT_SERVICE}/${src}`}
                        alt=""
                        text={item.itemDetail?.product?.name}
                    />
                );
            },
        },
        {
            key: 'type',
            header: 'Order Type',
        },
        {
            key: 'quantity',
            header: 'Quantity',
        },
        {
            key: 'price',
            header: 'Amount BHD',
            render: (item) => <span>{toFixedThree(item.price)}</span>,
        },
        {
            key: 'loyalty points',
            header: 'Loyalty Pts Received',
            render: (item) => item.itemDetail?.loyaltyPoints || 0,
        },
    ];

    const breadcrumb = [
        { label: 'Refunds', href: RoutesLink.refundReturn },
        { label: 'Details' },
    ];

    return (
        <Layout>
            <div className="flex items-center justify-between mb-6">
                <Breadcrumb items={breadcrumb} />
            </div>
            <div
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100"
            >
                <DetailsCard title="Refund Info" details={refundInfo} />
                <DetailsCard title="Customer Information" details={customerDetail} />
            </div>
            {states.refundDetail?.order?.orderItems.length && (
                <div className="my-4">
                    <Card>
                        <CommonTable
                            columns={productsColumn}
                            data={states?.refundDetail?.order?.orderItems}
                            minHeight="44"
                            actions={actions}
                        />
                    </Card>
                </div>
            )}
            {states?.refundDetail?.media &&
                states?.refundDetail?.media.length > 0 && (
                    <Card>
                        <h3 className="text-base text-black mb-5">Refund Images</h3>
                        <div>
                            <div
                                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mt-2"
                                uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100"
                            >
                                {states?.refundDetail?.media.map((item) => {
                                    return (
                                        <MediaListing
                                            BaseUrl={API_URL_ORDER_SERVICE}
                                            mediaUrl={item?.url}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </Card>
                )}
        </Layout>
    );
};

export default RefundDetail;
